const smartId = require('../../tools/smart-id')

const generateUnknownBatchForProduct = require('./virtual-batch').getUnknownBatchID

/**
 * Extracts the generic product ID from a batch ID.
 * @param {string} batchId - Batch ID in format "product:CODE:manufacturer:MANUFACTURER:batchNo:BATCH"
 * @returns {string} Product ID in format "product:CODE"
 * @example
 * batchIdToProductId('product:fds-aid0002:manufacturer:ghsc-aid0742a:batchNo:E252050')
 * // returns 'product:fds-aid0002'
 */
const batchIdToProductId = batchId => batchId.split(':').slice(0, 2).join(':')

const batchIdToManufacturerProductId = (batchId, { fallbackToBase = true } = {}) => {
  const match = batchId && batchId.match(/:manufacturer:([^:]+)/)
  if (match && match[1]) {
    return `product:${match[1]}`
  }
  return fallbackToBase ? batchIdToProductId(batchId) : undefined
}

/**
 * Extracts the manufacturer code from a batch ID.
 * @param {string} batchId - Batch ID in format "product:CODE:manufacturer:MANUFACTURER:batchNo:BATCH"
 * @returns {string|null} Manufacturer code or null if not found
 * @example
 * getManufacturerFromBatchId('product:fds-aid0002:manufacturer:ghsc-aid0742a:batchNo:E252050')
 * // returns 'ghsc-aid0742a'
 */
const getManufacturerFromBatchId = (batchId) => {
  if (!batchId || typeof batchId !== 'string') return null
  const parts = batchId.split(':')
  const manufacturerIndex = parts.indexOf('manufacturer')
  return manufacturerIndex !== -1 && parts[manufacturerIndex + 1]
    ? parts[manufacturerIndex + 1]
    : null
}

/**
 * Constructs a manufacturer-specific product ID from a batch ID.
 * For PSM tenant, the manufacturer code in the batch ID represents a product ID.
 * For other tenants, manufacturer is just metadata (e.g., "GSK") and this returns null.
 * @param {string} batchId - Batch ID in format "product:CODE:manufacturer:MANUFACTURER:batchNo:BATCH"
 * @returns {string|null} Manufacturer-specific product ID in format "product:MANUFACTURER" or null
 * @example
 * getManufacturerProductId('product:fds-aid0002:manufacturer:ghsc-aid0742a:batchNo:E252050')
 * // returns 'product:ghsc-aid0742a'
 */
const getManufacturerProductId = (batchId) => {
  const manufacturer = getManufacturerFromBatchId(batchId)
  return manufacturer ? `product:${manufacturer}` : null
}

const isBatchId = id => id.indexOf(':manufacturer:') !== -1 && id.indexOf(':batchNo:') !== -1

const isSnapshotId = id => id.indexOf(':agent:') > id.indexOf(':status:')

/**
 * Parses a batch ID into its product/manufacturer/batchNo parts. The batchNo is
 * extracted colon-safely (via `:batchNo:(.+)$`), so batchNos that themselves
 * contain a colon (e.g. '50766P1D1/SUB:1') are preserved rather than truncated
 * at the first colon by smart-id parse().
 * @param {string} batchId - Batch ID in format "product:CODE:manufacturer:MANUFACTURER:batchNo:BATCH"
 * @returns {{manufacturer: string, productId: string, batchNo: string}}
 * @example
 * parseBatchId('product:ghsc-mal0009:manufacturer:chemonics:batchNo:50766P1D1/SUB:1')
 * // returns { manufacturer: 'chemonics', productId: 'product:ghsc-mal0009', batchNo: '50766P1D1/SUB:1' }
 */
const parseBatchId = (batchId = '') => {
  const {product, manufacturer, batchNo: parsedBatchNo} = smartId.parse(batchId)
  const batchNoMatch = batchId.match(/:batchNo:(.+)$/)
  const batchNo = batchNoMatch ? batchNoMatch[1] : parsedBatchNo
  return {
    manufacturer,
    productId: `product:${product}`,
    batchNo
  }
}

module.exports = {
  generateUnknownBatchForProduct,
  batchIdToProductId,
  batchIdToManufacturerProductId,
  getManufacturerFromBatchId,
  getManufacturerProductId,
  parseBatchId,
  isBatchId,
  isSnapshotId
}
